import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// Fallback hardcoded map (used if API is unavailable)
const ROLE_MODULES = {
  super_admin: ['crm','hr','payroll','books','finance','inventory','distributors','budget','feedback','enterprise','tenders','investor','da','account_manager','sales_incentive'],
  admin:       ['crm','hr','payroll','books','finance','inventory','distributors','budget','feedback','enterprise','tenders','investor','da','account_manager','sales_incentive'],
  ceo:         ['crm','hr','payroll','books','finance','inventory','distributors','budget','feedback','enterprise','tenders','investor','da','account_manager','sales_incentive'],
  director:    ['crm','hr','payroll','books','finance','inventory','distributors','budget','feedback','enterprise','tenders','investor','da','account_manager','sales_incentive'],
  vp_sales:    ['crm','hr','payroll','finance','distributors','budget','feedback','enterprise','tenders','investor','da','account_manager','sales_incentive'],
  rsm:         ['crm','hr','payroll','feedback','enterprise','da','account_manager','sales_incentive'],
  sales_rep:   ['crm','hr','payroll'],
  bde:         ['crm','hr','payroll','da'],
  finance:     ['books','finance','hr','payroll','sales_incentive'],
  hr_admin:    ['hr','payroll'],
  dispatch:    ['inventory','hr','payroll'],
  employee:    ['hr','payroll'],
  account_manager: ['account_manager','hr','payroll'],
};

const MODULES = {
  crm: {
    id: 'crm',
    name: 'Sales CRM',
    icon: '📊',
    description: 'Doctors, accounts, visits, travel plans, targets & revenue intelligence',
    gradient: 'linear-gradient(135deg,#403EC9 0%,#6C63FF 100%)',
    color: '#403EC9',
    entry: '/dashboard',
    badge: null,
    features: ['Doctor CRM','Visit Tracking','Travel Plans','Revenue Reports'],
  },
  hr: {
    id: 'hr',
    name: 'HR',
    icon: '👥',
    description: 'Employees, attendance, leave, expenses & org management',
    gradient: 'linear-gradient(135deg,#059669 0%,#10B981 100%)',
    color: '#059669',
    entry: '/hr',
    badge: 'New',
    features: ['Employee Master','Attendance','Leave','Expenses'],
  },
  payroll: {
    id: 'payroll',
    name: 'Payroll',
    icon: '💰',
    description: 'Salary slips, payroll processing, PF/ESI/TDS & compliance',
    gradient: 'linear-gradient(135deg,#D97706 0%,#F59E0B 100%)',
    color: '#D97706',
    entry: '/payroll',
    badge: 'New',
    features: ['My Salary Slips','CTC Breakdown','Tax Deductions','Payroll Run'],
  },
  books: {
    id: 'books',
    name: 'Books',
    icon: '📒',
    description: 'Accounting, invoices, bills, bank reconciliation & GST compliance',
    gradient: 'linear-gradient(135deg,#0369A1 0%,#0EA5E9 100%)',
    color: '#0369A1',
    entry: '/books',
    badge: null,
    features: ['Invoices','Bills','Bank Reconciliation','GST & TDS'],
  },
  finance: {
    id: 'finance',
    name: 'Finance',
    icon: '🏦',
    description: 'Settlements, expense approvals, claims & financial reports',
    gradient: 'linear-gradient(135deg,#0F766E 0%,#14B8A6 100%)',
    color: '#0F766E',
    entry: '/finance',
    badge: null,
    features: ['Finance Dashboard','Settlements','Expense Approvals','Reports'],
  },
  inventory: {
    id: 'inventory',
    name: 'Inventory & Dispatch',
    icon: '📦',
    description: 'QC, dispatch operations, stock items, BOM, assembly orders & cost analysis',
    gradient: 'linear-gradient(135deg,#0D9488 0%,#0891B2 100%)',
    color: '#0D9488',
    entry: '/inventory',
    badge: null,
    features: ['QC & Dispatch','Stock Items','Bill of Materials','Cost & Margins'],
  },
  distributors: {
    id: 'distributors',
    name: 'Distributors',
    icon: '🏭',
    description: 'Distributor onboarding, orders, settlements & agreements',
    gradient: 'linear-gradient(135deg,#BE185D 0%,#EC4899 100%)',
    color: '#BE185D',
    entry: '/distributors',
    badge: null,
    features: ['Onboarding','Orders','Settlements','Agreements'],
  },
  budget: {
    id: 'budget',
    name: 'Budgets',
    icon: '📊',
    description: 'Annual operating budget — plan, phase, submit and approve company-wide spend',
    gradient: 'linear-gradient(135deg,#6D28D9 0%,#8B5CF6 100%)',
    color: '#6D28D9',
    entry: '/budget',
    badge: 'New',
    features: ['Annual Planning','Monthly Phasing','Submit & Approve','Territory Splits'],
  },
  feedback: {
    id: 'feedback',
    name: 'Feedback & NPS',
    icon: '⭐',
    description: 'Customer satisfaction, NPS scores, detractor alerts and feedback forms',
    gradient: 'linear-gradient(135deg,#403EC9 0%,#6C63FF 100%)',
    color: '#403EC9',
    entry: '/feedback',
    badge: 'New',
    features: ['NPS Dashboard','QR Feedback Forms','Detractor Alerts','Product Ratings'],
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise Sales',
    icon: '🏢',
    description: 'Strategic deal management — Kanban board, S3 documents, and annual targets',
    gradient: 'linear-gradient(135deg,#1E3A5F 0%,#2D5A8F 100%)',
    color: '#1E3A5F',
    entry: '/enterprise',
    badge: 'New',
    features: ['Deal Kanban Board','S3 Documents','Contact Management','Annual Targets'],
  },
  tenders: {
    id: 'tenders',
    name: 'Govt Tenders',
    icon: '📋',
    description: 'Government tender pipeline — GeM, CPPP, state portals, partner documents & invoices',
    gradient: 'linear-gradient(135deg,#1D4ED8 0%,#3B82F6 100%)',
    color: '#1D4ED8',
    entry: '/tenders',
    badge: 'New',
    features: ['Tender Board','Document Checklist','Partner Management','Invoices'],
  },
  investor: {
    id: 'investor',
    name: 'Investors',
    icon: '💼',
    description: 'Investor portal management — invitations, updates, performance notes, exit roadmap and cap table',
    gradient: 'linear-gradient(135deg,#403EC9 0%,#818CF8 100%)',
    color: '#403EC9',
    entry: '/investor',
    badge: 'New',
    features: ['Investor Access','Updates Feed','Performance Notes','Exit Roadmap'],
  },
  da: {
    id: 'da',
    name: 'Data Acquisition',
    icon: '🔍',
    description: 'Fetch pulmonologist & chest specialist listings from Google Places, verify records and enrich doctor & account data',
    gradient: 'linear-gradient(135deg,#5B21B6 0%,#7C3AED 100%)',
    color: '#7C3AED',
    entry: '/data-acquisition',
    badge: 'New',
    features: ['Google Places Fetch','Review Queue','Doctor Enrichment','Pincode Coverage'],
  },
  account_manager: {
    id: 'account_manager',
    name: 'Account Manager',
    icon: '🩺',
    description: 'Post-sale account ownership — device uptime, contract renewals, training, visits & feedback, and account health',
    gradient: 'linear-gradient(135deg,#C2410C 0%,#EA580C 100%)',
    color: '#C2410C',
    entry: '/account-manager',
    badge: 'New',
    features: ['Device Registry','Renewal Tracker','Training Log','Account Health'],
  },
  sales_incentive: {
    id: 'sales_incentive',
    name: 'Sales Incentives',
    icon: '🏆',
    description: 'Monthly incentive targets, achievement slabs, approvals & payouts for the sales team',
    gradient: 'linear-gradient(135deg,#A16207 0%,#EAB308 100%)',
    color: '#A16207',
    // Leadership-only for now (Phase 1) — lands on the team view. Once
    // reps get access in a later phase, this should route by role
    // instead (rep -> /incentives/mine, leadership -> /incentives/all).
    entry: '/incentives/all',
    badge: 'New',
    features: ['Monthly Targets','Achievement Slabs','Approvals','My Incentives'],
  },
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export default function PortalHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role_slug || user?.role || '';

  // null = still loading (prevents flash of wrong modules before server responds)
  const [allowedIds, setAllowedIds] = useState(null);

  useEffect(() => {
    api.get('/hr/portal-modules/my')
      .then(r => {
        if (r.data?.data && Array.isArray(r.data.data) && r.data.data.length > 0) {
          setAllowedIds(r.data.data);
        } else {
          setAllowedIds(ROLE_MODULES[role] || ['hr','payroll']);
        }
      })
      .catch(() => {
        setAllowedIds(ROLE_MODULES[role] || ['hr','payroll']);
      });
  }, [role]);

  const visibleModules = (allowedIds || []).map(id => MODULES[id]).filter(Boolean);

  return (
    <div style={{ minHeight:'100vh', background:'#F5F5FF', fontFamily:'DM Sans, sans-serif' }}>

      {/* Top bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'0 32px', height:60, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:30, height:30, borderRadius:8, background:'linear-gradient(135deg,#403EC9,#6C63FF)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          </div>
          <span style={{ fontWeight:800, fontSize:'1rem', color:'#111827', letterSpacing:'-0.02em' }}>
            alveofit<span style={{ color:'#403EC9' }}>®</span>
          </span>
          <span style={{ fontSize:'0.68rem', color:'#9CA3AF', fontWeight:600, background:'#F3F4F6', padding:'2px 8px', borderRadius:20, letterSpacing:'0.04em' }}>PORTAL</span>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:'0.82rem', fontWeight:600, color:'#111827' }}>{user?.name}</div>
            <div style={{ fontSize:'0.7rem', color:'#9CA3AF' }}>{user?.role_name || role}</div>
          </div>
          <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,#403EC9,#6C63FF)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.85rem' }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <button onClick={logout} style={{ background:'none', border:'1px solid #E5E7EB', borderRadius:8, padding:'5px 12px', fontSize:'0.78rem', color:'#6B7280', cursor:'pointer', fontWeight:500, fontFamily:'inherit' }}>
            Sign out
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth:1100, margin:'0 auto', padding:'40px 32px 60px' }}>

        {/* Greeting */}
        <div style={{ marginBottom:32 }}>
          <div style={{ fontSize:'0.78rem', color:'#9CA3AF', fontWeight:500, marginBottom:4 }}>
            {new Date().toLocaleDateString('en-IN',{ weekday:'long', day:'numeric', month:'long', year:'numeric' })}
          </div>
          <h1 style={{ fontSize:'1.6rem', fontWeight:800, color:'#111827', margin:'0 0 4px', letterSpacing:'-0.02em' }}>
            Good {getGreeting()}, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p style={{ color:'#6B7280', margin:0, fontSize:'0.9rem' }}>
            {allowedIds === null
              ? 'Loading your modules…'
              : visibleModules.length === 1
                ? 'Your module is ready below.'
                : `You have access to ${visibleModules.length} modules. Select one to get started.`}
          </p>
        </div>

        {/* Module tiles */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:20, marginBottom:40 }}>
          {allowedIds === null
            ? [1,2,3].map(i => (
                <div key={i} style={{ height:180, borderRadius:16, background:'#E5E7EB', animation:'pulse 1.5s infinite' }}/>
              ))
            : visibleModules.map(mod => (
                <ModuleTile key={mod.id} mod={mod} onClick={() => navigate(mod.entry)} />
              ))
          }
        </div>

        {/* Quick access */}
        <div style={{ borderTop:'1px solid #E5E7EB', paddingTop:28 }}>
          <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>
            Quick Access
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {[
              { label:'My Leaves',    icon:'🌴', path:'/hr/leave' },
              { label:'My Expenses',  icon:'🧾', path:'/hr/expenses' },
              { label:'Attendance',   icon:'📍', path:'/hr/attendance' },
              { label:'My Payslips',  icon:'💵', path:'/payroll/slips' },
              { label:'Tasks',        icon:'✅', path:'/tasks' },
              { label:'Inbox',        icon:'📬', path:'/inbox' },
            ].map(q => (
              <button key={q.path} onClick={() => navigate(q.path)}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'#fff', border:'1px solid #E5E7EB', borderRadius:20, fontSize:'0.8rem', color:'#374151', cursor:'pointer', fontWeight:500, fontFamily:'inherit' }}>
                <span>{q.icon}</span>{q.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ModuleTile({ mod, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden', cursor:'pointer', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', transition:'transform 0.15s ease, box-shadow 0.15s ease' }}
      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 12px 28px rgba(0,0,0,0.10)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.05)'; }}>

      {/* Header */}
      <div style={{ background:mod.gradient, padding:'22px 22px 18px', position:'relative' }}>
        <div style={{ fontSize:'2.2rem', marginBottom:6 }}>{mod.icon}</div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:'1.05rem', fontWeight:700, color:'#fff' }}>{mod.name}</span>
          {mod.badge && (
            <span style={{ fontSize:'0.58rem', fontWeight:700, background:'rgba(255,255,255,0.25)', color:'#fff', padding:'2px 7px', borderRadius:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>
              {mod.badge}
            </span>
          )}
        </div>
        <div style={{ position:'absolute', top:18, right:18, width:26, height:26, borderRadius:'50%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding:'14px 22px 18px' }}>
        <p style={{ margin:'0 0 12px', fontSize:'0.8rem', color:'#6B7280', lineHeight:1.55 }}>{mod.description}</p>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {mod.features.map(f => (
            <span key={f} style={{ fontSize:'0.68rem', padding:'3px 8px', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:6, color:'#6B7280', fontWeight:500 }}>{f}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
